import React from 'react';
import { ArrowLeft, ArrowRight } from 'lucide-react';

/**
 * About page (#about). Content from JSON (about).
 */
export default function AboutPage({ content, onBack }) {
  const about = content?.about || {};
  const headline = about.headline ?? '';
  const eyebrow = about.eyebrow ?? '';
  const founded = about.founded ?? '';
  const intro = about.intro ?? '';
  const story = Array.isArray(about.story) ? about.story : [];
  const discoverBlocks = Array.isArray(about.discoverBlocks) ? about.discoverBlocks : [];
  const jobHeading = about.jobOpeningsHeading ?? 'Join the team';
  const jobSubtext = about.jobOpeningsSubtext ?? '';
  const jobs = Array.isArray(about.jobOpenings) ? about.jobOpenings : [];

  return (
    <div className="min-h-screen bg-forest-bg relative overflow-hidden">
      {/* Ambient background glow */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            'radial-gradient(ellipse 60% 40% at 50% 5%, var(--jb-glow-cyan) 0%, transparent 70%), radial-gradient(ellipse 40% 30% at 85% 85%, var(--jb-glow-green) 0%, transparent 60%)',
        }}
        aria-hidden="true"
      />

      <div className="relative max-w-4xl mx-auto px-4 pt-24 pb-20">
        <button
          type="button"
          onClick={onBack}
          className="inline-flex items-center gap-2 font-body font-medium text-text-sub hover:text-cyan mb-10 transition"
        >
          <ArrowLeft className="w-4 h-4" /> Back to home
        </button>

        {/* Header */}
        <header className="mb-12">
          {eyebrow && (
            <div
              className="inline-flex items-center gap-2 py-1.5 px-3.5 rounded-full border font-mono text-[0.7rem] text-cyan uppercase tracking-[0.15em] mb-5"
              style={{ background: 'var(--jb-cyan-dim)', borderColor: 'var(--jb-border)' }}
            >
              <span className="w-1.5 h-1.5 rounded-full bg-cyan animate-pulse" />
              {eyebrow}
            </div>
          )}
          {headline && (
            <h1 className="font-display font-black text-3xl sm:text-4xl md:text-5xl text-text-hero mb-4 leading-tight">
              {headline}
            </h1>
          )}
          {founded && (
            <p className="font-mono text-text-muted text-sm uppercase tracking-widest">Est. {founded}</p>
          )}
        </header>

        {/* Intro */}
        {intro && (
          <p className="font-body text-text-body text-lg sm:text-xl leading-relaxed mb-14">
            {intro}
          </p>
        )}

        {/* Story — left accent border */}
        {story.length > 0 && (
          <div
            className="space-y-6 mb-16 pl-6 border-l-2"
            style={{ borderColor: 'var(--jb-border)' }}
          >
            {story.map((para, i) => (
              <p key={i} className="font-body text-text-sub leading-relaxed">
                {para}
              </p>
            ))}
          </div>
        )}

        {/* Discover blocks */}
        {discoverBlocks.length > 0 && (
          <section className="mb-16">
            <h2 className="font-display font-bold text-xl text-text-hero mb-6">Discover</h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {discoverBlocks.map((block, i) => (
                <div
                  key={i}
                  className="p-5 rounded-xl border bg-forest-card hover:border-cyan/30 transition group"
                  style={{ borderColor: 'var(--jb-border)' }}
                >
                  {block.tag && (
                    <span
                      className="inline-block font-mono text-[0.65rem] uppercase tracking-wider px-2 py-0.5 rounded-full mb-3"
                      style={{
                        background: i % 2 === 0 ? 'var(--jb-cyan-dim)' : 'var(--jb-accent-green-dim)',
                        color: i % 2 === 0 ? 'var(--jb-cyan)' : 'var(--jb-accent-green)',
                      }}
                    >
                      {block.tag}
                    </span>
                  )}
                  <h3 className="font-display font-bold text-text-hero mb-2 group-hover:text-cyan transition">
                    {block.title}
                  </h3>
                  <p className="font-body text-text-sub text-sm leading-relaxed">{block.description}</p>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Pioneer / Jobs */}
        {jobs.length > 0 && (
          <section>
            {/* Section hero card */}
            <div
              className="rounded-2xl border p-8 mb-6"
              style={{
                borderColor: 'var(--jb-border)',
                background: 'linear-gradient(135deg, var(--jb-cyan-dim) 0%, var(--jb-accent-green-dim) 100%)',
              }}
            >
              <span
                className="inline-block font-mono text-[0.65rem] uppercase tracking-wider px-2.5 py-1 rounded-full mb-4"
                style={{ background: 'var(--jb-cyan-dim)', color: 'var(--jb-cyan)' }}
              >
                We're hiring
              </span>
              <h2 className="font-display font-black text-2xl sm:text-3xl text-text-hero mb-3">
                {jobHeading}
              </h2>
              {jobSubtext && (
                <p className="font-body text-text-sub leading-relaxed max-w-2xl">{jobSubtext}</p>
              )}
            </div>

            {/* Job cards */}
            <div className="space-y-4">
              {jobs.map((job, i) => (
                <div
                  key={i}
                  className="p-5 rounded-xl border bg-forest-card flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 hover:border-cyan/30 transition"
                  style={{ borderColor: 'var(--jb-border)' }}
                >
                  <div>
                    <h3 className="font-display font-bold text-text-hero">{job.title}</h3>
                    {job.type && (
                      <span className="font-mono text-cyan text-xs">{job.type}</span>
                    )}
                    {job.description && (
                      <p className="font-body text-text-sub text-sm mt-2">{job.description}</p>
                    )}
                  </div>
                  {job.applyUrl && (
                    <a
                      href={job.applyUrl}
                      className="jb-cta inline-flex items-center gap-2 px-5 py-2.5 rounded-xl font-body font-bold text-forest-bg shrink-0 hover:opacity-90 transition"
                      style={{ background: 'linear-gradient(135deg, var(--jb-cyan), var(--jb-accent-green))' }}
                    >
                      Apply <ArrowRight className="w-4 h-4" />
                    </a>
                  )}
                </div>
              ))}
            </div>
          </section>
        )}
      </div>
    </div>
  );
}
